/**
 * Canonical equipment kind for AMS equipment (`assetType` / `hierarchyLevel`).
 *
 * Equipment used to inherit the raw asset display NAME at creation ("String
 * Inverter", "PV Array Module", even junk like "sdasd"), which every consumer
 * downstream — the mobile plant tree, the backend's string heuristics, sibling
 * sequence numbering — then had to fuzzy-match (GAP_REPORT P2-6). Derive a
 * canonical kind ONCE, at creation time: the asset's `moduleType` code is
 * authoritative when present, the name fragments cover the rest, and an
 * unknown type keeps its original name rather than being collapsed to a
 * misleading bucket.
 */
const MODULE_TYPE_KIND: Record<string, string> = {
  INV: 'Inverter',
  SCB: 'Combiner',
  MOD: 'Module',
  TRF: 'Transformer',
  CAB: 'Cable',
  MTB: 'Energy Meter',
  SWG: 'Switchgear',
};

// Checked in order — specific fragments before generic ones ('HT panel' is
// switchgear, not a PV panel; 'Module Array' must not collapse to 'Module').
const NAME_FRAGMENT_KIND: Array<[string, string]> = [
  ['inverter', 'Inverter'],
  ['combiner', 'Combiner'],
  ['ht panel', 'Switchgear'],
  ['modulegroup', 'Module Array'],
  ['array', 'Module Array'],
  ['module', 'Module'],
  ['panel', 'Module'],
  ['string', 'String'],
  ['transformer', 'Transformer'],
  ['meter', 'Energy Meter'],
  ['weather', 'Weather Station'],
  ['sensor', 'Sensor'],
  ['cable', 'Cable'],
  ['transmission', 'Cable'],
  ['switch', 'Switchgear'],
];

export function canonicalEquipmentKind(
  assetName?: string | null,
  moduleType?: string | null
): string {
  const byCode = moduleType ? MODULE_TYPE_KIND[moduleType.toUpperCase()] : undefined;
  if (byCode) return byCode;
  const name = (assetName ?? '').trim();
  const lower = name.toLowerCase();
  for (const [fragment, kind] of NAME_FRAGMENT_KIND) {
    if (lower.includes(fragment)) return kind;
  }
  // No canonical match — keep the original name (honest fallback; 'Other'
  // would erase information the raw label still carries).
  return name || 'Other';
}
